import { useTranslations } from '@/hooks/useTranslations';
import { DndContext, DragEndEvent, DragOverlay, PointerSensor, useDraggable, useDroppable, useSensor, useSensors } from '@dnd-kit/core';
import { Building2, Crown, GripVertical } from 'lucide-react';
import { useMemo, useState } from 'react';

export interface OrgUser {
    id: string;
    name: string;
    position: string | null;
    legal_entity_id: string | null;
    manager_id: string | null;
}

export interface OrgCompany {
    id: string;
    name: string;
    ceo_id: string | null;
    ceo_name: string | null;
}

interface CompanyOrgBuilderProps {
    users: OrgUser[];
    companies: OrgCompany[];
    onAssignManager: (userId: string, managerId: string | null) => void;
}

/**
 * Per-company approval tree. The CEO is the root; employees default to reporting
 * straight to the CEO. Drag an employee onto another to insert a manager
 * (User → Manager → CEO). Approval escalates up each level.
 */
export function CompanyOrgBuilder({ users, companies, onAssignManager }: CompanyOrgBuilderProps) {
    const { t } = useTranslations();
    const [activeId, setActiveId] = useState<string | null>(null);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

    const byId = useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);

    const descendantsOf = (id: string): Set<string> => {
        const out = new Set<string>();
        const walk = (mid: string) => {
            for (const child of users.filter((u) => u.manager_id === mid)) {
                if (!out.has(child.id)) {
                    out.add(child.id);
                    walk(child.id);
                }
            }
        };
        walk(id);
        return out;
    };

    const handleDragEnd = (event: DragEndEvent) => {
        setActiveId(null);
        const dragged = String(event.active.id);
        const over = event.over ? String(event.over.id) : null;
        if (!over) return;

        if (over.startsWith('root:')) {
            if (byId.get(dragged)?.manager_id != null) onAssignManager(dragged, null);
            return;
        }
        if (over === dragged) return;
        if (descendantsOf(dragged).has(over)) return;
        if ((byId.get(dragged)?.manager_id ?? null) === over) return;
        onAssignManager(dragged, over);
    };

    const unassigned = users.filter((u) => !u.legal_entity_id);
    const activeUser = activeId ? byId.get(activeId) : null;

    return (
        <DndContext
            sensors={sensors}
            onDragStart={(e) => setActiveId(String(e.active.id))}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setActiveId(null)}
        >
            <p className="text-muted-foreground mb-3 text-sm">{t('vacations.settings.org_chart_help')}</p>

            <div className="space-y-6">
                {companies.map((company) => {
                    const members = users.filter((u) => u.legal_entity_id === company.id && u.id !== company.ceo_id);
                    const roots = members.filter((u) => !u.manager_id || !members.some((m) => m.id === u.manager_id));
                    return (
                        <div key={company.id} className="rounded-lg border">
                            <div className="flex items-center gap-2 border-b p-3">
                                <Building2 className="text-muted-foreground h-4 w-4" />
                                <span className="text-sm font-semibold">{company.name}</span>
                                <span className="text-muted-foreground ml-auto flex items-center gap-1 text-xs">
                                    <Crown className="h-3 w-3" />
                                    {t('vacations.settings.ceo_label')}: {company.ceo_name ?? '—'}
                                </span>
                            </div>
                            <div className="p-3">
                                <RootZone companyId={company.id} label={t('vacations.settings.reports_to_ceo')} />
                                <div className="mt-2 space-y-2">
                                    {roots.map((user) => (
                                        <OrgNode key={user.id} user={user} members={members} depth={0} activeId={activeId} />
                                    ))}
                                    {members.length === 0 && <div className="text-muted-foreground px-2 py-2 text-xs">—</div>}
                                </div>
                            </div>
                        </div>
                    );
                })}

                {unassigned.length > 0 && (
                    <div className="rounded-lg border border-dashed">
                        <div className="text-muted-foreground border-b p-3 text-sm font-semibold">{t('vacations.settings.unassigned')}</div>
                        <div className="flex flex-wrap gap-2 p-3">
                            {unassigned.map((user) => (
                                <UserCard key={user.id} user={user} activeId={activeId} compact />
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <DragOverlay>
                {activeUser ? (
                    <div className="bg-background flex items-center gap-2 rounded-md border px-3 py-2 shadow-lg">
                        <GripVertical className="text-muted-foreground h-4 w-4" />
                        <span className="text-sm font-medium">{activeUser.name}</span>
                    </div>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
}

function RootZone({ companyId, label }: { companyId: string; label: string }) {
    const { setNodeRef, isOver } = useDroppable({ id: `root:${companyId}` });
    return (
        <div
            ref={setNodeRef}
            className={`flex items-center gap-2 rounded-md border border-dashed px-3 py-2 text-xs ${
                isOver ? 'border-primary bg-primary/5' : 'text-muted-foreground'
            }`}
        >
            <Crown className="h-3 w-3" />
            {label}
        </div>
    );
}

function OrgNode({ user, members, depth, activeId }: { user: OrgUser; members: OrgUser[]; depth: number; activeId: string | null }) {
    const reports = members.filter((m) => m.manager_id === user.id);
    return (
        <div style={{ marginLeft: depth > 0 ? 20 : 0 }}>
            <UserCard user={user} activeId={activeId} droppable />
            {reports.length > 0 && (
                <div className="mt-2 space-y-2 border-l pl-3">
                    {reports.map((report) => (
                        <OrgNode key={report.id} user={report} members={members} depth={depth + 1} activeId={activeId} />
                    ))}
                </div>
            )}
        </div>
    );
}

function UserCard({ user, activeId, compact, droppable }: { user: OrgUser; activeId: string | null; compact?: boolean; droppable?: boolean }) {
    const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({ id: user.id });
    const { setNodeRef: setDropRef, isOver } = useDroppable({ id: user.id });
    const setRef = (node: HTMLElement | null) => {
        setDragRef(node);
        if (droppable) setDropRef(node);
    };

    return (
        <div
            ref={setRef}
            className={`bg-background flex items-center gap-2 rounded-md border px-2 py-1.5 ${compact ? '' : 'w-full'} ${
                isOver && droppable && activeId !== user.id ? 'border-primary bg-primary/5' : ''
            } ${isDragging || activeId === user.id ? 'opacity-40' : ''}`}
        >
            <button
                {...listeners}
                {...attributes}
                type="button"
                className="text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing"
                aria-label="drag"
            >
                <GripVertical className="h-4 w-4" />
            </button>
            <div className="min-w-0">
                <div className="truncate text-sm font-medium">{user.name}</div>
                {user.position && !compact && <div className="text-muted-foreground truncate text-xs">{user.position}</div>}
            </div>
        </div>
    );
}
